import { useCallback, useEffect, useRef } from 'react';
import { notification } from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';
import React from 'react';
import { renderPermissionLink } from '@op/components/ReportPermission/utils/permissionNotification';
import { useReportPermissionNotify } from '@op/components/ReportPermission';

interface UseReportCreateSuccessOptions {
  /** 自定义通知文案，传入后覆盖默认的中英双语提示 */
  message?: string;
  /** ScrollIntoView 配置，用于覆盖默认滚动行为 */
  scrollOptions?: ScrollIntoViewOptions;
  /**
   * 触发滚动的条件。
   * 当此值从 falsy 变为 true 时，执行延迟滚动 + 飘字通知。
   */
  trigger: boolean;
  /**
   * 首次滚动前的延迟（ms），用于等待 React 渲染提交和布局完成。
   * @default 150
   */
  delay?: number;
  /**
   * DOM 元素未就绪时的最大重试次数。
   * @default 5
   */
  maxRetries?: number;
  /**
   * 每次重试的间隔（ms）。
   * @default 200
   */
  retryInterval?: number;
  /**
   * 重新触发键。当此值变化时，重置已触发标记，允许再次滚动。
   * 适用于同一页面重复提交的场景（如 GenerateSummary）。
   * 传入每次生成的新 taskId 即可。
   */
  scrollKey?: string | number;
}

interface UseReportCreateSuccessReturn {
  /** 挂载到目标 DOM 元素上的 ref */
  reportRef: React.RefObject<HTMLDivElement>;
}

const DEFAULT_MESSAGE = {
  zh: '已自动定位到报告模块，你也可以前往"AI 任务中心"查看已创建报告',
  en: 'Automatically navigated to the Report module. You can also go to the "AI Task Center" to view created reports.',
};

/**
 * 报告创建成功 Hook
 *
 * 功能：
 * 1. 在指定条件满足后，自动平滑滚动到目标 DOM 区域
 * 2. 右上角弹出成功飘字提示
 * 3. 内置延迟 + 重试机制，确保 DOM 渲染完成后再滚动
 *
 * @example
 * ```tsx
 * // 非 Topic 分析：内容区块渲染完成后滚动
 * const { reportRef } = useReportCreateSuccess({
 *   trigger: showChildren,
 * });
 *
 * // Topic 分析：数据打标板块出现时滚动
 * const { reportRef } = useReportCreateSuccess({
 *   trigger: isTopicAnalysis,
 *   delay: 300, // 给步骤动画预留更多时间
 * });
 *
 * <div ref={reportRef}>
 *   <ReportContent />
 * </div>
 * ```
 */
export default function useReportCreateSuccess(
  options: UseReportCreateSuccessOptions,
): UseReportCreateSuccessReturn {
  const { isEn, formatMessage } = useLanguage();
  const {
    message,
    scrollOptions,
    trigger,
    delay = 150,
    maxRetries = 5,
    retryInterval = 200,
    scrollKey,
  } = options;

  const resolvedMessage = message || DEFAULT_MESSAGE[isEn ? 'en' : 'zh'];

  // 从 Context 读取权限目标（创建报告时由 Filter 写入），弹通知时打开权限面板
  const permissionNotify = useReportPermissionNotify();
  const openByTargetRef = useRef(permissionNotify?.openByTarget);
  openByTargetRef.current = permissionNotify?.openByTarget;
  const consumePendingReportTargetRef = useRef(
    permissionNotify?.consumePendingReportTarget,
  );
  consumePendingReportTargetRef.current =
    permissionNotify?.consumePendingReportTarget;

  const reportRef = useRef<HTMLDivElement>(null);
  const hasTriggeredRef = useRef(false);
  const retryCountRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notifyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 用 ref 存放配置值，避免对象引用变化导致 effect 重复执行
  const scrollOptionsRef = useRef(scrollOptions);
  scrollOptionsRef.current = scrollOptions;
  const messageRef = useRef(resolvedMessage);
  messageRef.current = resolvedMessage;
  const delayRef = useRef(delay);
  delayRef.current = delay;
  const maxRetriesRef = useRef(maxRetries);
  maxRetriesRef.current = maxRetries;
  const retryIntervalRef = useRef(retryInterval);
  retryIntervalRef.current = retryInterval;

  const cleanupTimers = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // scrollKey 变化时重置触发标记
  useEffect(() => {
    hasTriggeredRef.current = false;
  }, [scrollKey]);

  useEffect(() => {
    if (!trigger) {
      cleanupTimers();
      return;
    }

    if (hasTriggeredRef.current) return;

    hasTriggeredRef.current = true;
    retryCountRef.current = 0;

    const tryScroll = (): void => {
      if (reportRef.current) {
        reportRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          ...scrollOptionsRef.current,
        });

        // 滚动动画结束后弹出通知（不受 trigger 变 false 影响）
        notifyTimerRef.current = setTimeout(() => {
          // 消费暂存的权限目标（创建报告时由 Filter 写入 Context）
          const permissionTarget =
            consumePendingReportTargetRef.current?.() ?? null;
          notification.success({
            description: (
              <div>
                <span className="dbo-report-permission-notify">
                  {formatMessage(
                    {
                      id: 'The report task has been created and saved to the AI Task Center. By default, it is visible only to you; you can adjust this by clicking on the {permission} settings.',
                    },
                    {
                      permission: renderPermissionLink(() => {
                        if (permissionTarget && openByTargetRef.current) {
                          openByTargetRef.current(permissionTarget);
                        }
                      }, formatMessage({ id: 'Permissions' })),
                    },
                  )}
                </span>
                <p></p>
                {/* 定位文案 */}
                {/* {messageRef.current} */}
              </div>
            ),
          });
        }, 600);
        return;
      }

      retryCountRef.current += 1;
      if (retryCountRef.current < maxRetriesRef.current) {
        timerRef.current = setTimeout(tryScroll, retryIntervalRef.current);
      }
    };

    timerRef.current = setTimeout(tryScroll, delayRef.current);
  }, [trigger, scrollKey, cleanupTimers]);

  // 组件卸载时清理所有定时器
  useEffect(() => {
    return () => {
      cleanupTimers();
      if (notifyTimerRef.current !== null) {
        clearTimeout(notifyTimerRef.current);
        notifyTimerRef.current = null;
      }
    };
  }, [cleanupTimers]);

  return { reportRef };
}
